const customTemplate = `<style>
  :host {
    display: block;
    width: 100%;
  }

  .hide {
    display: none;
  }

  .message {
    bottom: var(--phone-message-bottom, auto);
    left: var(--phone-message-left, 0);
    margin: 0;
    position: var(--phone-message-position, static);
    right: 0;
    text-align: start;
    z-index: 1;
  }

  .message--error {
    color: var(--color-error);
  }

  .message--valid {
    color: var(--color-success);
  }
</style>
<slot></slot>
<p class="message js-message visually-hidden" role="status"></p>`

class PhoneInput extends HHBaseElement {
  constructor() {
    super(customTemplate)
    this._validate = this._validate.bind(this)
    this._handleFocus = this._handleFocus.bind(this)
    this._handleFocusOut = this._handleFocusOut.bind(this)
    this._errorClass = 'message--error'
    this._hiddenClass = 'hide'
    this._validClass = 'message--valid'
    this._deactivateHandler = null
  }

  connectedCallback() {
    const slotElements = this._shadowRoot.querySelector('slot').assignedElements()
    const foundInputs = slotElements.filter(el => el.tagName === 'INPUT')
    if (foundInputs.length === 0) {
      return
    }

    this.input = foundInputs[0]
    this.infoMessage = this._shadowRoot.querySelector('.js-message')
    this._addEventListeners()

    this._validator = window.intlTelInput(this.input, {
      initialCountry: window.shopLocale?.toLocaleLowerCase() || 'auto',
      hiddenInput: this.getAttribute('mod-name') || 'phone',
      preferredCountries: window.shopLocale ? [window.shopLocale.toLocaleLowerCase()] : [],
      utilsScript: window.phoneInputUtilsFile
    })
    this._validator.promise.then(() => {
      this._flagSelectedElement = this.querySelector('.iti__flag-container')
      this._setHiddenInputAsRequired()
      this._modifyHiddenInputValue()
      this._setFlagSelectorClick()
    })
  }

  disconnectedCallback() {
    this.input.removeEventListener('change', this._validate)
    this.input.removeEventListener('focus', this._handleFocus)
    this.input.removeEventListener('blur', this._handleFocusOut)
    this.input.removeEventListener('keyup', this._validate)
    this.previousElementSibling.removeEventListener(
      'click',
      this._handleFocus
    )
    this._validator.destroy()
  }

  _modifyHiddenInputValue(valid = true) {
    const extraInput = this.querySelector('input[type="hidden"]')
    if (!extraInput) {
      return
    }

    if (!valid) {
      extraInput.value = ''
      return
    }
    extraInput.value = this._validator.getNumber()
  }

  _setHiddenInputAsRequired() {
    const extraInput = this.querySelector('input[type="hidden"]')
    if (!extraInput) {
      return
    }

    extraInput.required = true
  }

  _setFlagSelectorClick() {
    this._flagSelectedElement.addEventListener('click', (ev) => {
      ev.preventDefault()
      if (this._deactivateHandler) {
        clearTimeout(this._deactivateHandler)
        this._deactivateHandler = null
      }

      const inputContainer = this._flagSelectedElement.closest('.js-input-container')
      inputContainer.classList.add('active')
      inputContainer.querySelector('label')?.classList.add('active')
    })

    this._flagSelectedElement.addEventListener('blur', () => {
      const inputContainer = this._flagSelectedElement.closest('.js-input-container')
      inputContainer.classList.remove('active')
      inputContainer.querySelector('label')?.classList.remove('active')
    })
  }

  _reset() {
    this.infoMessage.classList.remove(this._errorClass, this._validClass)
    this.infoMessage.innerHTML = ''
    this.infoMessage.classList.add(this._hiddenClass)
  }

  _validate() {
    this._reset()
    if (!this.input.value.trim()) {
      return
    }

    const isValid = this._validator.isPossibleNumber()
    this._modifyHiddenInputValue(isValid)
    if (isValid) {
      this.infoMessage.classList.add(this._validClass, this._hiddenClass)
      this.infoMessage.innerHTML = window.phoneInputMessages.valid
      return
    }

    const errorCode = this._validator.getValidationError()
    this.infoMessage.innerHTML = window.phoneInputMessages.errors[errorCode < 0 ? 0 : errorCode]
    this.infoMessage.classList.add(this._errorClass)
    this.infoMessage.classList.remove(this._validClass, this._hiddenClass)
  }

  _addEventListeners() {
    this.input.addEventListener('change', this._validate)
    this.input.addEventListener('focus', this._handleFocus)
    this.input.addEventListener('blur', this._handleFocusOut)
    this.input.addEventListener('keyup', this._validate)
  }

  _handleFocus() {
    this._activate()
    this.input.focus()
  }

  _handleFocusOut() {
    this._deactivateHandler = setTimeout(() => {
      this._deactivate()
      clearTimeout(this._deactivateHandler)
      this._deactivateHandler = null
    }, 100)
  }

  _activate() {
    this.previousElementSibling.classList.add('active')
  }

  _deactivate() {
    if (this.input.value === '') {
      this.previousElementSibling.classList.remove('active')
      this.classList.remove('active')
    }
  }
}

window.customElements.define('phone-input', PhoneInput)
